import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { BucketS3Service } from './bucket-s3.service';
import { Meta, Title } from '@angular/platform-browser';
import { environment } from '@environments/environment';

interface Metadata {
  title: string;
  description: string;
  relativeUrl: string;
  shouldIndex?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class SeoMetadataService {

  // https://stackoverflow.com/questions/48330535/dynamically-add-meta-description-based-on-route-in-angular
  // https://seoalive.com/meta-tags/

  constructor(
    private meta: Meta,
    private title: Title,
    private S3Service: BucketS3Service,
    @Inject(DOCUMENT) private dom: Document
  ) { }

  setMetadata(meta: Metadata) {

    this.title.setTitle(meta.title);
    this.meta.updateTag({ property: 'og:title', content: meta.title });

    this.meta.updateTag({ name: 'description', content: meta.description });
    this.meta.updateTag({ property: 'og:description', content: meta.description });

    const URL = `${environment.BASE_URL}${meta.relativeUrl}`;
    this.updateCanonicalUrl(URL);
    this.meta.updateTag({ property: 'og:url', content: URL });

    const robotsContent = meta.shouldIndex === false ? 'noindex, nofollow' : 'index, follow';
    this.meta.updateTag({ name: 'robots', content: robotsContent });
  }

  updateCanonicalUrl(url: string): void {
    let link: HTMLLinkElement | null = this.dom.querySelector("link[rel='canonical']");

    if (link) {
      link.setAttribute('href', url);
    } else {
      link = this.dom.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', url);
      this.dom.head.appendChild(link);
    }
  }

  createPhysicalBookSchema(book: any, authorsList: any[], type: string, binding: string, edition: string): object {
    const authors = (authorsList || [])
      .filter((a: any) => a.role === "Autor")
      .map((a: any) => ({
        "@type": "Person",
        "name": `${a.firstname} ${a.lastname}`
      }));

    return {
      "@context": "http://schema.org/",
      "@type": ["Book", "Product"],
      "sku": book.bookcode,
      "name": book.title,
      "description": book.subtitle || book.summary || book.description || book.title,
      "brand": {
        "@type": "Brand",
        "name": "Editorial Astrea"
      },
      "image": this.S3Service.getImageURL(book.image),
      "author": authors,
      "offers": {
        "@type": "Offer",
        "url": `https://astrea.com.ar/book/${book.bookcode}`,
        "price": book.price,
        "priceCurrency": "ARS",
        "availability": book.available ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        "itemCondition": "https://schema.org/NewCondition",
        "eligibleQuantity": {
          "@type": "QuantitativeValue",
          "minValue": 1,
          "unitCode": "EA"
        }
      },
      "additionalProperty": [
        {
          "@type": "PropertyValue",
          "name": "Editorial",
          "value": book.vEditorial.editorial,
        },
        {
          "@type": "PropertyValue",
          "name": "Tipo de obra",
          "value": type,
        },
        {
          "@type": "PropertyValue",
          "name": "Cantidad de páginas",
          "value": book.pages,
        },
        {
          "@type": "PropertyValue",
          "name": "Edición",
          "value": edition,
        },
        {
          "@type": "PropertyValue",
          "name": "Tipo de tapa",
          "value": binding,
        },
        {
          "@type": "PropertyValue",
          "name": "Formato",
          "value": book.bookFormat,
        },
        {
          "@type": "PropertyValue",
          "name": "Fecha de publicación",
          "value": book.publishDate,
        },
        {
          "@type": "PropertyValue",
          "name": "Año de publicación",
          "value": book.year,
        },
        {
          "@type": "PropertyValue",
          "name": "ISBN",
          "value": book.isbn,
        },
      ]
    };
  }

  createDigitalBookSchema(book: any, authorsList: any[], avPrice: number, type: string, edition: string): object {
    const authors = (authorsList || [])
      .filter((a: any) => a.role === "Autor")
      .map((a: any) => ({
        "@type": "Person",
        "name": `${a.firstname} ${a.lastname}`
      }));

    return {
      "@context": "http://schema.org/",
      "@type": ["Book", "Product"],
      "sku": book.bookcode,
      "name": `${book.title} (Digital)`,
      "author": authors,
      "description": book.subtitle || book.summary || book.description || book.title,
      "brand": {
        "@type": "Brand",
        "name": "Editorial Astrea"
      },
      "image": this.S3Service.getImageURL(book.image),
      "offers": {
        "@type": "Offer",
        "url": `https://astrea.com.ar/book/${book.bookcode}`,
        "price": avPrice,
        "priceCurrency": "ARS",
        "availability": "https://schema.org/InStock",
        "priceSpecification": {
          "@type": "UnitPriceSpecification",
          "price": avPrice,
          "priceCurrency": "ARS",
          "billingDuration": 3
        }
      },
      "additionalProperty": [
        {
          "@type": "PropertyValue",
          "name": "Tipo de obra",
          "value": type,
        },
        {
          "@type": "PropertyValue",
          "name": "Edición",
          "value": edition,
        },
        {
          "@type": "PropertyValue",
          "name": "Formato",
          "value": "Acceso digital"
        },
        {
          "@type": "PropertyValue",
          "name": "Fecha de publicación",
          "value": book.publishDate,
        },
        {
          "@type": "PropertyValue",
          "name": "Año de publicación",
          "value": book.year,
        },
        {
          "@type": "PropertyValue",
          "name": "ISBN",
          "value": book.isbn,
        }
      ]
    };
  }

  createAuthorSchema(author: any): object {
    return {
      "@context": "https://schema.org",
      "@type": "Person",
      "name": `${author.firstname} ${author.lastname}`,
      "givenName": author.firstname,
      "familyName": author.lastname,
      "identifier": author.authorcode,
      "worksFor": {
        "@type": "Organization",
        "name": "Editorial Astrea"
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `https://astrea.com.ar/author/${author.authorcode}`
      }
    };
  }

  createEditorialSchema(editorial: any): object {
    return {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": editorial.editorial,
      "url": `https://astrea.com.ar/editorial/${editorial.url}`,
    };
  }

}
